import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Broadcast, CircleNotch, ArrowRight, ArrowLeft, Check, Buildings, UserCircle, Palette } from "@phosphor-icons/react";

const TIMEZONES = ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"];
const RESOLUTIONS = ["720p", "1080p", "1440p", "4K"];
const STEPS = [
  { id: 1, label: "Organization", icon: Buildings },
  { id: 2, label: "Studio Owner", icon: UserCircle },
  { id: 3, label: "Branding", icon: Palette },
];

export default function Setup() {
  const { setup } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    organization_name: "", first_name: "", last_name: "", email: "", password: "", confirm: "",
    logo: null, description: "", default_timezone: "UTC", default_resolution: "1080p",
    contact_email: "", contact_phone: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo: reader.result });
    reader.readAsDataURL(file);
  };

  const validateStep = () => {
    setError("");
    if (step === 1 && !form.organization_name.trim()) return "Organization name is required";
    if (step === 2) {
      if (!form.first_name.trim() || !form.last_name.trim()) return "First and last name are required";
      if (!form.email.trim()) return "Email is required";
      if (form.password.length < 6) return "Password must be at least 6 characters";
      if (form.password !== form.confirm) return "Passwords do not match";
    }
    return "";
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setStep(step + 1);
  };

  const finish = async () => {
    setError(""); setLoading(true);
    try {
      const { confirm, ...payload } = form;
      await setup({ ...payload, default_output: "HLS" });
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6">
      <div className="w-full max-w-2xl ccs-enter">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-9 w-9 bg-[#3A86FF] flex items-center justify-center rounded-sm">
            <Broadcast size={22} weight="fill" />
          </div>
          <div>
            <div className="font-head font-black text-lg tracking-tight leading-none">Channel Command Studio</div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#737373] mt-1">FIRST-TIME SETUP</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8" data-testid="setup-steps">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 ${step >= s.id ? "text-white" : "text-[#525252]"}`}>
                <div className={`h-8 w-8 rounded-sm flex items-center justify-center border ${step > s.id ? "bg-emerald-600 border-emerald-600" : step === s.id ? "bg-[#3A86FF] border-[#3A86FF]" : "border-[#262626]"}`}>
                  {step > s.id ? <Check size={16} weight="bold" /> : <s.icon size={16} />}
                </div>
                <span className="text-xs font-medium hidden sm:block">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.id ? "bg-emerald-600" : "bg-[#262626]"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-head font-bold text-lg">Create your organization</h2>
              <p className="text-sm text-[#737373] -mt-2">This is your studio workspace.</p>
              <Field label="Organization name" value={form.organization_name} onChange={set("organization_name")} testid="setup-org-name" placeholder="e.g. ReVine Studios" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Description (optional)</label>
                <textarea data-testid="setup-org-desc" value={form.description} onChange={set("description")} rows={2}
                  className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none resize-none" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-head font-bold text-lg">Studio Owner account</h2>
              <p className="text-sm text-[#737373] -mt-2">The Owner has full, permanent control of the platform.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" value={form.first_name} onChange={set("first_name")} testid="setup-first-name" />
                <Field label="Last name" value={form.last_name} onChange={set("last_name")} testid="setup-last-name" />
              </div>
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="setup-email" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="setup-password" />
                <Field label="Confirm password" type="password" value={form.confirm} onChange={set("confirm")} testid="setup-confirm" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-head font-bold text-lg">Branding & defaults</h2>
              <p className="text-sm text-[#737373] -mt-2">Optional. You can change these later in Settings.</p>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Organization logo</label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-sm border border-[#262626] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                    {form.logo ? <img src={form.logo} alt="logo" className="max-h-full max-w-full object-contain" /> : <Buildings size={24} className="text-[#525252]" />}
                  </div>
                  <input data-testid="setup-logo" type="file" accept="image/*" onChange={onLogo} className="text-xs text-[#A3A3A3]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Default time zone</label>
                  <select data-testid="setup-timezone" value={form.default_timezone} onChange={set("default_timezone")}
                    className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none">
                    {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Default resolution</label>
                  <select data-testid="setup-resolution" value={form.default_resolution} onChange={set("default_resolution")}
                    className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none">
                    {RESOLUTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact email (optional)" type="email" value={form.contact_email} onChange={set("contact_email")} testid="setup-contact-email" />
                <Field label="Contact phone (optional)" value={form.contact_phone} onChange={set("contact_phone")} testid="setup-contact-phone" />
              </div>
            </div>
          )}

          {error && <div data-testid="setup-error" className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-sm mt-4">{error}</div>}

          <div className="flex items-center justify-between mt-6">
            <button data-testid="setup-back" disabled={step === 1 || loading} onClick={() => setStep(step - 1)}
              className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white disabled:opacity-30 transition-colors duration-150">
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 ? (
              <button data-testid="setup-next" onClick={next}
                className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium px-5 py-2.5 rounded-sm">
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="setup-finish" disabled={loading} onClick={finish}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 transition-colors duration-150 text-white font-medium px-5 py-2.5 rounded-sm disabled:opacity-60">
                {loading && <CircleNotch size={16} className="animate-spin" />} Finish & Launch
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150 placeholder:text-[#525252]" />
    </div>
  );
}
